import {
  ComponentFactoryResolver,
  ComponentRef,
  Directive,
  ElementRef, EventEmitter,
  HostListener,
  Injector,
  Input, NgZone,
  OnDestroy, Output,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { DropdownContainerComponent } from '../components/dropdown-modal-decorator/dropdown-container.component';
import { UtilService } from '../services/util.service';

export type eDropdownDirection = 'auto' | 'top' | 'bottom' | 'left' | 'right' | 'topLeft' | 'bottomRight' | 'topRight';

@Directive({
    selector: '[fkDropdown]',
})
export class DropdownDirective implements OnDestroy {
  @Input() set fkDropdown(value: boolean) {
      if (value !== this._isOpen) {
        this._isOpen = value;
        this.createOrDestroy();
      }
    }

  @Input() fkDropdownTemplate!: TemplateRef<any>;
  @Input() fkDropdownDirection: eDropdownDirection = 'auto';
  @Input() fkHasBaseClass: boolean = true;
  @Input() fkDropdownWidthAsEl: boolean = false;
  @Input() fkDropdownClickElTrigger: boolean = true;

  @Output() clickOutside: EventEmitter<void> = new EventEmitter();

  private componentRef?: ComponentRef<DropdownContainerComponent>;
  private static activeComponent?: ElementRef;
  private _isOpen: boolean = false;

  private boundUpdatePosition = this.updatePosition.bind(this);
  private boundHandleClickOutside = this.handleClickOutside.bind(this);

  private positionCalculators = {
    bottom: (el: DOMRect) => ({ left: `${el.left}px`, top: `${el.bottom}px` }),
    top: (el: DOMRect, dropdown: DOMRect) => ({ left: `${el.left}px`, top: `${el.top - dropdown.height}px` }),
    left: (el: DOMRect, dropdown: DOMRect) => ({ left: `${el.left - dropdown.width}px`, top: `${el.top}px` }),
    right: (el: DOMRect) => ({ left: `${el.right}px`, top: `${el.top}px` }),
    topLeft: (el: DOMRect, dropdown: DOMRect) => ({ left: `${el.left - dropdown.width}px`, top: `${el.top - dropdown.height}px` }),
    bottomRight: (el: DOMRect, dropdown: DOMRect) => ({ left: `${el.left - (dropdown.width - el.width)}px`, top: `${el.bottom}px` }),
    topRight: (el: DOMRect, dropdown: DOMRect) => ({ left: `${el.right + 8}px`, top: `${el.bottom - dropdown.bottom}px` }),
  };

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private viewContainerRef: ViewContainerRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private injector: Injector,
    private ngZone: NgZone,
) {}

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    event.stopPropagation();

    if (this.fkDropdownClickElTrigger) {
      this._isOpen = !this._isOpen;

      this.createOrDestroy();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.componentRef && this._isOpen) {
      DropdownDirective.activeComponent?.nativeElement.click();
      this.destroyComponent();
      this.clickOutside.emit();
    }
  }

  private createOrDestroy(): void {
    if (this._isOpen) {
      DropdownDirective.activeComponent?.nativeElement.click();
      this.createDropdownMenu();
    } else {
      this.destroyComponent();
    }
  }

  private createDropdownMenu(): void {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(DropdownContainerComponent);
    this.componentRef = this.viewContainerRef.createComponent(componentFactory, 0, this.injector);
    this.renderer.setStyle(this.componentRef.location.nativeElement, 'visibility', 'hidden');

    DropdownDirective.activeComponent = this.el;

    Object.assign(this.componentRef.instance, {
      direction: this.fkDropdownDirection,
      hasBaseClass: this.fkHasBaseClass,
      content: this.fkDropdownTemplate,
    });

    if (this.fkDropdownWidthAsEl) {
      this.renderer.setStyle(
        this.componentRef.location.nativeElement, 'width', `${this.el.nativeElement.clientWidth}px`
      );
    }
    this.renderer.setStyle(this.componentRef.location.nativeElement, 'z-index', '100');

    document.body.appendChild(this.componentRef?.location.nativeElement);

    setTimeout(() => {
      const target = this.getScrollableParent(this.el.nativeElement);

      this.calculateDropDownPosition(target);

      this.renderer.setStyle(this.componentRef?.location.nativeElement, 'visibility', 'visible');
    }, 0);

    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('scroll', this.boundUpdatePosition);
      document.body.addEventListener('scroll', this.boundUpdatePosition, true);
    });
    document.addEventListener('click', this.boundHandleClickOutside);
  }

  private calculateDropDownPosition(container?: HTMLElement) {
      const dropdownDictionary = {
        top: this.positionCalculators['top'],
        left: this.positionCalculators['left'],
        right: this.positionCalculators['right'],
        bottom: this.positionCalculators['bottom'],
        topLeft: this.positionCalculators['topLeft'],
        default: this.positionCalculators['bottom'],
      }
      const elPosition = this.el.nativeElement.getBoundingClientRect();
      const dropdownPosition = this.componentRef!.location.nativeElement.getBoundingClientRect();

      if (this.fkDropdownDirection === 'auto') {
        const position =
          UtilService.getAutoPlacement(this.el, this.componentRef!, dropdownDictionary, container)(elPosition, dropdownPosition);

        Object.keys(position).forEach(item => {
          this.renderer.setStyle(this.componentRef?.location.nativeElement, item, position[item]);
        });
      } else {
        const position: Record<string, string> =
          this.positionCalculators[this.fkDropdownDirection](elPosition, dropdownPosition);

        Object.keys(position).forEach(item => {
          this.renderer.setStyle(this.componentRef?.location.nativeElement, item, position[item]);
        });
      }
      }

  private updatePosition(event: Event) {
    if (!this.componentRef) return;

    const target = event.target as HTMLElement;
    if (this.isInsideComponent(target)) return;
    if (!target.scrollTop) return;

    this.calculateDropDownPosition(target);

    const targetRect = target.getBoundingClientRect();
    const componentElement = this.componentRef.location.nativeElement;
    const componentRect = componentElement.getBoundingClientRect();
    this.applyClipping(targetRect, componentRect, componentElement);
  }

  private applyClipping(targetRect: DOMRect, componentRect: DOMRect, componentElement: HTMLElement) {
    const topInset = Math.max(-50, targetRect.top - componentRect.top);
    const bottomInset = Math.max(-50, componentRect.bottom - targetRect.bottom);

    if (topInset > 0 || bottomInset > 0) {
      this.renderer.setStyle(
        componentElement,
        'clip-path',
        `inset(${topInset}px -50px ${bottomInset}px -50px)`
      );
    } else {
      this.renderer.removeStyle(componentElement, 'clip-path');
    }
  }

  private handleClickOutside(event: MouseEvent): void {
    if (!this.componentRef) return;

    const dropdownElement = this.componentRef.location.nativeElement;
    const targetElement = event.target as HTMLElement;

    if (targetElement.closest('[not-outside="true"]')) {
      return;
    }

    if (!dropdownElement.contains(targetElement)) {
      DropdownDirective.activeComponent?.nativeElement.click();
      this.destroyComponent();
      this.clickOutside.emit();
    }
  }

  private isInsideComponent(target: HTMLElement): boolean {
    return this.componentRef?.location.nativeElement.contains(target) || false;
  }

  private getScrollableParent(element: HTMLElement | null): HTMLElement | undefined {
    while (element) {
      const style = window.getComputedStyle(element);
      const hasOverflow = style.overflow === 'auto' || style.overflowY === 'auto' || style.overflowX === 'auto';

      if (hasOverflow && (element.scrollHeight > element.clientHeight + 25 || element.scrollWidth > element.clientWidth + 25)) {
        return element;
      }
      element = element.parentElement;
    }
    return undefined;
  }

  private destroyComponent() {
      if (this.componentRef) {
        DropdownDirective.activeComponent = undefined;
        this.componentRef.destroy();
        document.removeEventListener('click', this.boundHandleClickOutside);
        window.removeEventListener('scroll', this.boundUpdatePosition);
        document.body.removeEventListener('scroll', this.boundUpdatePosition, true);
      }
    }
  ngOnDestroy() {
    this.destroyComponent();
  }
}
